import { CopyDayFailure, CopyDayProblem, CopyDayRequest, CopyDayResult } from '../types';
import { isCanonicalDate } from '../utils/calendarDate';

export interface StoredCopyDayOperation extends CopyDayRequest {
  accountKey: string;
  contractVersion: 1;
  state: 'uncertain' | 'copied' | 'noRecords' | 'rejected' | 'notSubmitted';
  result?: CopyDayResult;
  problem?: CopyDayProblem;
  needsRefresh?: boolean;
  message?: string;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const prefix = (account: string) => `copyDay.v1:${account}:`;
export const copyDayStorageKey = (operation: StoredCopyDayOperation) => `${prefix(operation.accountKey)}${operation.operationId}`;
export const copyDayCommand = (operation: CopyDayRequest): CopyDayRequest => ({
  operationId: operation.operationId, sourceDate: operation.sourceDate, destinationDate: operation.destinationDate,
});

const validReasons = (value: unknown) => Array.isArray(value) && value.every(reason => reason &&
  typeof reason === 'object' && typeof reason.code === 'string' && typeof reason.message === 'string');

const validProblem = (problem: CopyDayProblem) => problem && typeof problem.code === 'string' &&
  ['notSubmitted', 'pending', 'rejected', 'unknown'].includes(problem.mutationOutcome) && typeof problem.retrySameOperation === 'boolean' &&
  (problem.errors === undefined || validReasons(problem.errors));

const validFailure = (failure: CopyDayFailure) => failure && failure.code === 'SOURCE_VALIDATION_FAILED' &&
  typeof failure.message === 'string' && validReasons(failure.errors) &&
  Number.isSafeInteger(failure.validationFailureCount) && failure.validationFailureCount >= 0 && typeof failure.errorsTruncated === 'boolean';

export const validateCopyDayResult = (value: unknown, command: CopyDayRequest): CopyDayResult | null => {
  if (!value || typeof value !== 'object') return null;
  const result = value as CopyDayResult;
  if (result.operationId !== command.operationId || result.sourceDate !== command.sourceDate ||
    result.destinationDate !== command.destinationDate || result.contractVersion !== 1 ||
    !['copied', 'noRecords', 'rejected'].includes(result.outcome) ||
    !Number.isSafeInteger(result.copiedCount) || result.copiedCount < 0 ||
    (result.outcome === 'copied' ? result.copiedCount === 0 : result.copiedCount !== 0) ||
    typeof result.completedAtUtc !== 'string' || !Number.isFinite(Date.parse(result.completedAtUtc)) ||
    typeof result.replayed !== 'boolean' ||
    (result.outcome === 'rejected' && (!result.error || !validFailure(result.error)))) return null;
  return { ...copyDayCommand(result), contractVersion: 1, outcome: result.outcome, copiedCount: result.copiedCount,
    completedAtUtc: result.completedAtUtc, replayed: result.replayed, ...(result.error ? { error: result.error } : {}) };
};

const validateStored = (value: unknown, account: string): value is StoredCopyDayOperation => {
  if (!value || typeof value !== 'object') return false;
  const op = value as StoredCopyDayOperation;
  return op.accountKey === account && UUID.test(op.operationId) && op.operationId !== '00000000-0000-0000-0000-000000000000' &&
    op.contractVersion === 1 && isCanonicalDate(op.sourceDate) && isCanonicalDate(op.destinationDate) && op.sourceDate !== op.destinationDate &&
    ['uncertain', 'copied', 'noRecords', 'rejected', 'notSubmitted'].includes(op.state) &&
    (!['copied', 'noRecords'].includes(op.state) || (!!op.result && !!validateCopyDayResult(op.result, op) && op.result.outcome === op.state)) &&
    (op.state !== 'rejected' || (!!op.result && !!validateCopyDayResult(op.result, op) && op.result.outcome === 'rejected') ||
      (!!op.problem && validProblem(op.problem) && op.problem.code === 'SOURCE_VALIDATION_FAILED' && op.problem.mutationOutcome === 'rejected')) &&
    (op.state !== 'notSubmitted' || (!!op.problem && validProblem(op.problem) && op.problem.mutationOutcome === 'notSubmitted' &&
      ['INVALID_REQUEST', 'INVALID_DATE', 'SAME_DATE'].includes(op.problem.code)));
};

export const readCopyDayOperations = (account: string): StoredCopyDayOperation[] => {
  try {
    const result: StoredCopyDayOperation[] = [];
    for (let index = 0; index < localStorage.length; index++) {
      const key = localStorage.key(index);
      if (!key?.startsWith(prefix(account))) continue;
      const value = JSON.parse(localStorage.getItem(key) || 'null');
      if (!validateStored(value, account) || copyDayStorageKey(value) !== key) throw new Error('Invalid descriptor');
      result.push(value);
    }
    return result;
  } catch {
    throw new Error('Copy recovery storage could not be read. Restore browser storage access before copying; retain existing recovery data.');
  }
};

export const saveCopyDayOperation = (operation: StoredCopyDayOperation): StoredCopyDayOperation => {
  if (!validateStored(operation, operation.accountKey)) throw new Error('Invalid copy recovery context');
  const key = copyDayStorageKey(operation);
  let stored: StoredCopyDayOperation | undefined;
  try {
    const raw = localStorage.getItem(key);
    stored = raw ? JSON.parse(raw) : undefined;
  } catch {
    throw new Error('Copy recovery storage is unavailable. Enable browser storage and try again.');
  }
  if (stored && (!validateStored(stored, operation.accountKey) || stored.sourceDate !== operation.sourceDate ||
    stored.destinationDate !== operation.destinationDate || stored.contractVersion !== operation.contractVersion)) {
    throw new Error('Copy operation dates and identity are immutable. Recover the original operation.');
  }
  // A delayed uncertain response in another tab must not erase a known terminal outcome.
  if (stored && stored.state !== 'uncertain' && operation.state === 'uncertain') return stored;
  try {
    localStorage.setItem(key, JSON.stringify(operation));
    if (localStorage.getItem(key) !== JSON.stringify(operation)) throw new Error('Storage did not retain descriptor');
    return operation;
  } catch {
    throw new Error('Copy recovery storage is unavailable. Enable browser storage and try again.');
  }
};

export const acknowledgeCopyDay = (operation: StoredCopyDayOperation) => {
  if (operation.state === 'uncertain') throw new Error('An unresolved copy cannot be discarded. Recover the same operation.');
  if (operation.needsRefresh) throw new Error('Retry the Dashboard refresh before acknowledging this copy.');
  localStorage.removeItem(copyDayStorageKey(operation));
};

/** Only the documented definite failures authorize correcting dates with a new ID. */
export const definiteCopyDayProblem = (error: unknown, operation: CopyDayRequest): CopyDayProblem | null => {
  const response = (error as { response?: { status?: number; data?: CopyDayProblem } })?.response;
  const problem = response?.data;
  if (!problem || !validProblem(problem) || (problem.operationId && problem.operationId !== operation.operationId)) return null;
  if (response?.status === 400 && problem.mutationOutcome === 'notSubmitted' &&
      ['INVALID_REQUEST', 'INVALID_DATE', 'SAME_DATE'].includes(problem.code)) return problem;
  if (response?.status === 422 && problem.mutationOutcome === 'rejected' && problem.code === 'SOURCE_VALIDATION_FAILED') return problem;
  return null;
};

export const copyDayProblemMessage = (problem?: CopyDayProblem | CopyDayFailure) => {
  if (!problem) return '';
  const reasons = problem.errors?.map(error => error.message).filter(Boolean) || [];
  const detail = ('message' in problem ? problem.message : problem.detail || problem.title) || 'The copy was rejected. Check the source entries and selected dates.';
  return [detail, ...reasons, problem.errorsTruncated ? `${problem.validationFailureCount ?? 'More'} validation failures were reported.` : ''].filter(Boolean).join(' ');
};
